import type { IncompletePeriods } from '@/shared/lib/incomplete-periods';
import {
  CategoryScale,
  type ChartData,
  Chart as ChartJS,
  type ChartOptions,
  Filler,
  Legend,
  type LegendOptions,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  incompletePeriodPlugin,
  useIncompletePeriodsOptions,
} from './incomplete-period-plugin';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MultiLineChartProps {
  data: ChartData<'line'>;
  yLabel?: string;
  legendProps?: Partial<LegendOptions<'line'>>;
  optionsProps?: ChartOptions<'line'>;
  incompletePeriods?: IncompletePeriods;
}

const MultiLineChart: React.FC<MultiLineChartProps> = ({
  data,
  yLabel,
  legendProps = {},
  optionsProps = {},
  incompletePeriods,
}) => {
  const incompletePeriodsOptions =
    useIncompletePeriodsOptions(incompletePeriods);
  const basePlugins: NonNullable<ChartOptions<'line'>['plugins']> = {
    legend: {
      display: false,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        boxWidth: 14,
      },
      ...legendProps,
    },
    tooltip: { enabled: true },
    datalabels: { display: false },
  };
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: { color: '#eee' },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: '#eee' },
        title: {
          display: !!yLabel,
          text: yLabel,
          padding: { top: 8, bottom: 0 },
          color: '#666',
          font: { size: 12 },
        },
      },
    },
    elements: {
      line: {
        borderWidth: 2,
        tension: 0,
        fill: true,
      },
      point: {
        radius: 5,
        borderWidth: 2,
        backgroundColor: '#fff', // hollow style
      },
    },
    ...optionsProps,
  };
  // `...optionsProps` swaps `plugins` wholesale, so put the defaults back and
  // let the caller's entries (e.g. a tooltip title) win per plugin.
  options.plugins = {
    ...basePlugins,
    ...optionsProps.plugins,
    incompletePeriods: incompletePeriodsOptions,
  };

  // 🔖 Plugin to draw flags at custom indices
  // const flagPlugin = {
  //   id: 'flagPlugin',
  //   afterDatasetsDraw: (chart: any) => {
  //     const { ctx } = chart;
  //     ctx.save();

  //     const flagIndices = [2, 5, 7, 9]; // indices to draw flags (customize!)

  //     chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
  //       const meta = chart.getDatasetMeta(datasetIndex);
  //       if (!meta.data) return;

  //       flagIndices.forEach((i) => {
  //         const point = meta.data[i];
  //         if (!point) return;

  //         const { x, y } = point.getProps(['x', 'y'], true);

  //         // Draw red circle
  //         ctx.beginPath();
  //         ctx.arc(x, y, 12, 0, 2 * Math.PI);
  //         ctx.fillStyle = '#fff';
  //         ctx.fill();
  //         ctx.lineWidth = 2;
  //         ctx.strokeStyle = '#F44336';
  //         ctx.stroke();

  //         // Draw flag icon (simple rectangle+triangle as placeholder)
  //         ctx.fillStyle = '#F44336';
  //         ctx.fillRect(x - 4, y - 6, 8, 4);
  //         ctx.beginPath();
  //         ctx.moveTo(x + 4, y - 6);
  //         ctx.lineTo(x + 10, y - 4);
  //         ctx.lineTo(x + 4, y - 2);
  //         ctx.closePath();
  //         ctx.fill();
  //       });
  //     });

  //     ctx.restore();
  //   },
  // };

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <Line data={data} options={options} plugins={[incompletePeriodPlugin]} />
    </div>
  );
};

export { MultiLineChart };
